import React, { useEffect, useMemo, useState } from "react";
import { SubmitData } from "../../services/apiService";
import Likert from "./components/Likert";
import Bar from "./components/Bar";
import "./SymptomScoringFlow.scss";
import useAlertUtils from "../../utils/common.utils";

const BASE_DOMAINS = [
  { key: "energy", label: "Low energy / afternoon crashes" },
  { key: "mood", label: "Mood changes / irritability" },
  { key: "sleep", label: "Trouble falling or staying asleep" },
  { key: "libido", label: "Low libido / sexual function changes" },
  { key: "weight", label: "Weight gain / difficulty losing" },
  { key: "brainfog", label: "Brain fog / difficulty concentrating" },
  { key: "stress", label: "High stress / feeling overwhelmed" },
];

const FEMALE_EXTRA = [
  { key: "hotflashes", label: "Hot flashes / night sweats" },
  { key: "cycle", label: "Irregular / changing cycles" },
  { key: "vaginal", label: "Vaginal dryness / discomfort" },
  { key: "hairskin", label: "Hair thinning / skin changes" },
];

const MALE_EXTRA = [
  { key: "motivation", label: "Low motivation / drive" },
  { key: "ed", label: "Erectile performance changes" },
  { key: "recovery", label: "Poor workout recovery / strength" },
  { key: "waist", label: "Increasing waist size" },
];

export default function SymptomScoringFlow() {
  const [step, setStep] = useState(1);
  const [gender, setGender] = useState(null);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const { alertSuccess, alertError } = useAlertUtils();
  const isLoggedIn = Boolean(
    typeof window !== "undefined" && localStorage.getItem("token")
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);

  const fields = useMemo(() => {
    if (!gender) return [];
    return gender === "female"
      ? [...BASE_DOMAINS, ...FEMALE_EXTRA]
      : [...BASE_DOMAINS, ...MALE_EXTRA];
  }, [gender]);

  const total = useMemo(
    () => Object.values(answers).reduce((a, b) => a + (b || 0), 0),
    [answers]
  );
  const maxTotal = useMemo(() => fields.length * 5, [fields.length]);
  const rating = useMemo(() => {
    if (total <= 9) return { label: "Balanced", color: "balanced" };
    if (total <= 19) return { label: "Mild Imbalance", color: "mild" };
    if (total <= 34) return { label: "Moderate Imbalance", color: "moderate" };
    return { label: "Significant Imbalance", color: "significant" };
  }, [total]);

  const domainScores = useMemo(() => {
    const groups =
      gender === "female"
        ? {
            Energy: ["energy", "stress"],
            Mood: ["mood", "brainfog"],
            Sleep: ["sleep"],
            Metabolic: ["weight"],
            SexHormones: [
              "libido",
              "hotflashes",
              "cycle",
              "vaginal",
              "hairskin",
            ],
          }
        : {
            Energy: ["energy", "stress"],
            Mood: ["mood", "brainfog"],
            Sleep: ["sleep"],
            Metabolic: ["weight", "waist"],
            SexHormones: ["libido", "motivation", "ed", "recovery"],
          };
    const out = [];
    Object.entries(groups).forEach(([label, keys]) => {
      const v = keys.reduce((sum, k) => sum + (answers[k] || 0), 0);
      const max = keys.length * 5;
      out.push({ label, value: v, max });
    });
    return out;
  }, [answers, gender]);

  const groupedQuestions = useMemo(() => {
    if (!gender) return [];
    const labelMap = Object.fromEntries(fields.map((f) => [f.key, f.label]));
    const groups =
      gender === "female"
        ? {
            Energy: ["energy", "stress"],
            Mood: ["mood", "brainfog"],
            Sleep: ["sleep"],
            Metabolic: ["weight"],
            SexHormones: [
              "libido",
              "hotflashes",
              "cycle",
              "vaginal",
              "hairskin",
            ],
          }
        : {
            Energy: ["energy", "stress"],
            Mood: ["mood", "brainfog"],
            Sleep: ["sleep"],
            Metabolic: ["weight", "waist"],
            SexHormones: ["libido", "motivation", "ed", "recovery"],
          };
    return Object.entries(groups)
      .map(([group, keys]) => ({
        group,
        items: keys
          .map((k) => (labelMap[k] ? { key: k, label: labelMap[k] } : null))
          .filter(Boolean),
      }))
      .filter((g) => g.items.length > 0);
  }, [gender, fields]);

  const startOver = () => {
    setStep(1);
    setGender(null);
    setAnswers({});
    setEmail("");
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem("symptoms_score");
      if (saved) {
        const d = JSON.parse(saved);
        if (d && typeof d === "object") {
          if (d.gender) setGender(d.gender);
          if (d.answers) setAnswers(d.answers);
          setStep(3);
        }
      }
    } catch (_) {
      alertError("Error parsing saved symptoms score");
    } finally {
      localStorage.removeItem("symptoms_score");
    }
  }, []);

  const saveScoresToLocalStorage = () => {
    try {
      const getKeys = () => {
        if (gender === "female")
          return [...BASE_DOMAINS, ...FEMALE_EXTRA].map((f) => f.key);
        if (gender === "male")
          return [...BASE_DOMAINS, ...MALE_EXTRA].map((f) => f.key);
        return [...BASE_DOMAINS, ...FEMALE_EXTRA, ...MALE_EXTRA].map(
          (f) => f.key
        );
      };
      const fullAnswers = Object.fromEntries(
        getKeys().map((k) => [
          k,
          answers && typeof answers[k] === "number" ? answers[k] : 0,
        ])
      );
      const payload = {
        gender,
        email,
        total,
        maxTotal,
        ratingLabel: rating.label,
        domainScores,
        answers: fullAnswers,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem("symptoms_score", JSON.stringify(payload));
    } catch (e) {}
  };

  const submitAssessment = async () => {
    setIsSubmitting(true);
    try {
      const getUserIdFromToken = () => {
        try {
          const t =
            typeof window !== "undefined"
              ? localStorage.getItem("token")
              : null;
          if (!t) return null;
          const parts = t.split(".");
          if (parts.length < 2) return null;
          const payload = JSON.parse(
            atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
          );
          return payload.userId || payload.sub || payload.id || null;
        } catch (e) {
          return null;
        }
      };
      const userId = getUserIdFromToken();
      const domainMap = Object.fromEntries(
        domainScores.map((d) => [d.label, { value: d.value, max: d.max }])
      );
      const getKeys = () => {
        if (gender === "female")
          return [...BASE_DOMAINS, ...FEMALE_EXTRA].map((f) => f.key);
        if (gender === "male")
          return [...BASE_DOMAINS, ...MALE_EXTRA].map((f) => f.key);
        return [...BASE_DOMAINS, ...FEMALE_EXTRA, ...MALE_EXTRA].map(
          (f) => f.key
        );
      };
      const fullAnswers = Object.fromEntries(
        getKeys().map((k) => [
          k,
          answers && typeof answers[k] === "number" ? answers[k] : 0,
        ])
      );
      const payload = {
        userId: userId,
        genderAtEntry: gender,
        entryDate: new Date().toISOString().slice(0, 10),
        answers: JSON.stringify(fullAnswers),
        domainScores: JSON.stringify(domainMap),
        totalScore: total,
        maxTotal: maxTotal,
        ratingLabel: rating.label,
      };
      const res = await SubmitData(
        "/api/assessments/create-assessment",
        payload
      );
      try {
        if (res && (res.success === true || res.status === true)) {
          alertSuccess(res.message || "Assessment saved successfully.");
        } else {
          alertError((res && res.message) || "Failed to save assessment.");
        }
      } catch (_) {}
      return res;
    } catch (e) {
      try {
        alertError("Failed to save assessment.");
      } catch (_) {}
      return null;
    } finally {
      setIsSubmitting(false);
      window.location.href = "/app/tracker-history";
    }
  };

  const submitAssessmentEmail = async () => {
    setIsEmailSubmitting(true);
    try {
      const domainMap = Object.fromEntries(
        domainScores.map((d) => [d.label, { value: d.value, max: d.max }])
      );
      const getKeys = () => {
        if (gender === "female")
          return [...BASE_DOMAINS, ...FEMALE_EXTRA].map((f) => f.key);
        if (gender === "male")
          return [...BASE_DOMAINS, ...MALE_EXTRA].map((f) => f.key);
        return [...BASE_DOMAINS, ...FEMALE_EXTRA, ...MALE_EXTRA].map(
          (f) => f.key
        );
      };
      const fullAnswers = Object.fromEntries(
        getKeys().map((k) => [
          k,
          answers && typeof answers[k] === "number" ? answers[k] : 0,
        ])
      );
      const payload = {
        email,
        genderAtEntry: gender,
        entryDate: new Date().toISOString().slice(0, 10),
        answers: JSON.stringify(fullAnswers),
        domainScores: JSON.stringify(domainMap),
        totalScore: total,
        maxTotal: maxTotal,
        ratingLabel: rating.label,
      };
      const res = await SubmitData(
        "/api/assessments/send-data-to-mail",
        payload
      );
      try {
        if (res && (res.success === true || res.status === true)) {
          alertSuccess(res.message || "Email sent successfully.");
        } else {
          alertError((res && res.message) || "Failed to send email.");
        }
      } catch (_) {}
      return res;
    } catch (e) {
      try {
        alertError("Failed to send email.");
      } catch (_) {}
      return null;
    } finally {
      setIsEmailSubmitting(false);
      window.location.href = "/app/symptoms-tracker";
    }
  };

  return (
    <div className="ss-container">
      <div className="ss-card">
        <div className="ss-card__header">
          <div className="ss-hero">
            <div className="ss-hero__text">
              <h1 className="ss-card__title">Hormone Health Symptom Score</h1>
              <p className="ss-card__subtitle">
                Answer a few quick questions to get your personalized score and
                insights. It takes ~2 minutes.
              </p>
              <div className="ss-steps">
                <div
                  className={`ss-step ${step >= 1 ? "is-done" : ""} ${
                    step === 1 ? "is-active" : ""
                  }`}
                >
                  <div className="ss-step__bullet">1</div>
                  <div className="ss-step__label">Profile</div>
                </div>
                <div
                  className={`ss-step ${step >= 2 ? "is-done" : ""} ${
                    step === 2 ? "is-active" : ""
                  }`}
                >
                  <div className="ss-step__bullet">2</div>
                  <div className="ss-step__label">Questions</div>
                </div>
                <div
                  className={`ss-step ${step >= 3 ? "is-done" : ""} ${
                    step === 3 ? "is-active" : ""
                  }`}
                >
                  <div className="ss-step__bullet">3</div>
                  <div className="ss-step__label">Results</div>
                </div>
                <div
                  className={`ss-step ${step >= 4 ? "is-done" : ""} ${
                    step === 4 ? "is-active" : ""
                  }`}
                >
                  <div className="ss-step__bullet">4</div>
                  <div className="ss-step__label">Email</div>
                </div>
              </div>
            </div>
            <div className="ss-hero__art" aria-hidden="true">
              <div className="ss-blob ss-blob--1"></div>
              <div className="ss-blob ss-blob--2"></div>
              <div className="ss-hero__ring"></div>
              <div className="ss-hero__ring ss-hero__ring--2"></div>
            </div>
          </div>
        </div>
        <div className="ss-card__content">
          {step === 1 && (
            <div>
              <h2 className="ss-heading ss-heading--center">
                Who should we tailor this for?
              </h2>
              <div className="ss-grid ss-grid--two">
                <button
                  onClick={() => {
                    setGender("female");
                    setStep(2);
                  }}
                  className={`ss-choice ${
                    gender === "female" ? "is-selected" : ""
                  }`}
                  aria-pressed={gender === "female"}
                >
                  <div className="ss-choice__row">
                    <div className="ss-choice__icon" aria-hidden="true">
                      <svg
                        width="36"
                        height="36"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 14a6 6 0 1 0 -0.001 -12.001A6 6 0 0 0 12 14Z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                        />
                        <path
                          d="M12 14v3M12 21v-4M12 17H9M12 17h3"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div>
                      <div className="ss-choice__title">Women</div>
                      <div className="ss-choice__desc">
                        Menopause, perimenopause, thyroid, adrenal
                      </div>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setGender("male");
                    setStep(2);
                  }}
                  className={`ss-choice ${
                    gender === "male" ? "is-selected" : ""
                  }`}
                  aria-pressed={gender === "male"}
                >
                  <div className="ss-choice__row">
                    <div className="ss-choice__icon" aria-hidden="true">
                      <svg
                        width="36"
                        height="36"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20 4h-5M20 4v5M20 4l-6.5 6.5"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                        <circle
                          cx="9"
                          cy="15"
                          r="5.5"
                          stroke="currentColor"
                          strokeWidth="1.6"
                        />
                      </svg>
                    </div>
                    <div>
                      <div className="ss-choice__title">Men</div>
                      <div className="ss-choice__desc">
                        Testosterone optimization, thyroid, adrenal
                      </div>
                    </div>
                  </div>
                </button>
              </div>
              <div className="ss-center ss-mt">
                <button
                  className="ss-btn ss-btn--primary"
                  onClick={() => (gender ? setStep(2) : null)}
                  disabled={!gender}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {step === 2 && gender && (
            <div>
              <div className="ss-row ss-row--space">
                <h2 className="ss-heading">
                  Tell us how often you experience the following:
                </h2>
                <span className="ss-muted">0 = Never, 5 = Severe / Daily</span>
              </div>
              <div className="ss-questions">
                {groupedQuestions.map((g) => (
                  <div key={g.group} className="ss-section">
                    <div className="ss-section__title">{g.group}</div>
                    {g.items.map((f) => (
                      <div key={f.key} className="ss-question">
                        <div className="ss-question__label">{f.label}</div>
                        <Likert
                          value={answers[f.key] ?? 0}
                          onChange={(v) =>
                            setAnswers((s) => ({ ...s, [f.key]: v }))
                          }
                        />
                        <div className="ss-scale">
                          <span>Never</span>
                          <span>Sometimes</span>
                          <span>Severe</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="ss-actions-sticky">
                <div className="ss-actions-sticky__inner">
                  <button
                    className="ss-btn ss-btn--ghost"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </button>
                  <button
                    className="ss-btn ss-btn--primary"
                    onClick={() => setStep(3)}
                  >
                    See My Score
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="ss-results__intro">
                <div className={`ss-chip ss-chip--${rating.color}`}>
                  Your Rating:{" "}
                  <span
                    className={`ss-chip__value ss-chip__value--${rating.color}`}
                  >
                    {rating.label}
                  </span>
                </div>
                <h2 className="ss-score">
                  {total} <span className="ss-score__max">/ {maxTotal}</span>
                </h2>
                <p className="ss-muted">
                  Higher scores suggest more pronounced symptoms that may
                  benefit from hormone, thyroid, or adrenal evaluation.
                </p>
              </div>

              <div className="ss-grid ss-grid--two ss-gap">
                <div className="ss-panel">
                  <h3 className="ss-panel__title">Your Domains</h3>
                  {domainScores.map((d) => (
                    <Bar
                      key={d.label}
                      label={d.label}
                      value={d.value}
                      max={d.max}
                    />
                  ))}
                </div>
                <div className="ss-panel">
                  <h3 className="ss-panel__title">Recommended Next Step</h3>
                  <ul className="ss-list">
                    <li>
                      Create your HRT.org account to save your score and view a
                      personalized plan.
                    </li>
                    <li>
                      {gender === "female"
                        ? "Consider a Menopause / Women’s Hormone Panel."
                        : "Consider a Male Optimization Panel."}
                    </li>
                    <li>
                      Telehealth consult with a licensed provider to review
                      options.
                    </li>
                  </ul>
                  <div className="ss-note">
                    This score is for wellness guidance and is not a diagnosis.
                  </div>
                </div>
              </div>

              <div className="ss-grid ss-grid--two ss-actions">
                {isLoggedIn ? (
                  <div className="ss-grid ss-grid--two ss-actions">
                    <button
                      className="ss-btn ss-btn--primary ss-btn--lg m-auto"
                      onClick={() => {
                        if (isSubmitting) return;
                        submitAssessment();
                      }}
                      disabled={isSubmitting}
                      aria-busy={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="ss-spinner"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        "Save My Score"
                      )}
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      className="ss-btn ss-btn--primary ss-btn--lg"
                      onClick={() => {
                        saveScoresToLocalStorage();
                        window.location.href = "/register";
                      }}
                    >
                      Create Account & Save My Score
                    </button>
                    <button
                      className="ss-btn ss-btn--outline ss-btn--lg"
                      onClick={() => setStep(4)}
                    >
                      Continue Without Account (Next)
                    </button>
                  </>
                )}
              </div>

              <div className="ss-center">
                <button className="ss-link" onClick={() => setStep(2)}>
                  Edit my answers
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="ss-heading ss-heading--center">
                Get Your Results by Email
              </h2>
              <p className="ss-muted ss-muted--center">
                We’ll send your score and next steps. You can finish creating
                your account later.
              </p>
              <div className="ss-form">
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="ss-input"
                />
                <div className="ss-row ss-row--center ss-mt">
                  <button
                    className="ss-btn ss-btn--ghost"
                    onClick={() => setStep(3)}
                  >
                    Back
                  </button>
                  <button
                    className="ss-btn ss-btn--success"
                    onClick={() => {
                      if (isEmailSubmitting) return;
                      submitAssessmentEmail();
                    }}
                    disabled={isEmailSubmitting || !email}
                    aria-busy={isEmailSubmitting}
                  >
                    {isEmailSubmitting ? (
                      <>
                        <span className="ss-spinner" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : (
                      "Send Score To Your Email"
                    )}
                  </button>
                </div>
              </div>
              <div className="ss-center">
                <button className="ss-link" onClick={startOver}>
                  Start over
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
